import type { Gift } from '@/entities/cart'

export type GiftWithQuantity = Gift & { quantity: number }

export const isGift = (item: any): item is Gift => {
	return (
		item &&
		typeof item.ID === 'number' &&
		typeof item.KOD === 'string' &&
		typeof item.TITLE === 'string' &&
		Array.isArray(item.PRODUCT_IDS) &&
		typeof item.IMAGE === 'string'
	)
}

export const dedupeById = <T extends { ID: number }>(arr: T[]): T[] => {
	return Array.from(new Map(arr.map(g => [g.ID, g])).values())
}

export const aggregateGiftsById = (items: GiftWithQuantity[]): GiftWithQuantity[] => {
	const byId = new Map<number, GiftWithQuantity>()
	for (const g of items) {
		const prev = byId.get(g.ID)
		if (prev) byId.set(g.ID, { ...prev, quantity: prev.quantity + g.quantity })
		else byId.set(g.ID, g)
	}
	return Array.from(byId.values())
}

export const canIncludeAllForProduct = (params: {
	isAuto: boolean
	excludedAllFlag: boolean
	hasOne: boolean
	hasSelected: boolean
}): boolean => {
	const { isAuto, excludedAllFlag, hasSelected } = params
	return isAuto && !excludedAllFlag && !hasSelected
}

export const computeCheckoutGifts = (args: {
	products: Array<{ PRODUCT_ID: number; QUANTITY: number; IS_SERVICE?: boolean }>
	one: Gift[]
	all: Gift[]
	selectedGiftsByProduct: Record<number, Gift | undefined>
	excludedAllFlagByProduct: Record<number, boolean>
	excludedIdsByProduct: Record<number, number[]>
	allGiftsByProduct: Record<number, Gift[]>
}): GiftWithQuantity[] => {
	const {
		products,
		one,
		all,
		selectedGiftsByProduct,
		excludedAllFlagByProduct,
		excludedIdsByProduct,
		allGiftsByProduct
	} = args

	const result: GiftWithQuantity[] = []

	for (const product of products) {
		const productOne = one.filter(g => g.PRODUCT_IDS.includes(product.PRODUCT_ID))
		// Prefer applied auto gifts from the store for this product; fallback to API 'all'
		const storeAllList = allGiftsByProduct[product.PRODUCT_ID] || []
		const sourceAll = storeAllList.length ? storeAllList : all
		const productAll = sourceAll.filter(g => g.PRODUCT_IDS.includes(product.PRODUCT_ID))

		// add selected ONE
		const selected = selectedGiftsByProduct[product.PRODUCT_ID]
		if (selected && productOne.find(g => g.ID === selected.ID)) {
			result.push({ ...selected, quantity: product.QUANTITY })
		}

		// add ALL if allowed
		const isAuto = (allGiftsByProduct[product.PRODUCT_ID]?.length ?? 0) > 0 || productAll.length > 0
		const excludedAllFlag = Boolean(excludedAllFlagByProduct[product.PRODUCT_ID])
		const hasOne = productOne.length > 0
		const hasSelected = Boolean(selected)
		if (canIncludeAllForProduct({ isAuto, excludedAllFlag, hasOne, hasSelected })) {
			const excludedIds = new Set(excludedIdsByProduct[product.PRODUCT_ID] || [])
			const uniqAll = dedupeById(productAll)
			for (const g of uniqAll) if (!excludedIds.has(g.ID)) result.push({ ...g, quantity: product.QUANTITY })
		}
	}

	return aggregateGiftsById(result)
}

// Build backend payload for GIFT field: { one: number[]; all: number[] }
// Returns strictly { one: number[]; all: number[] }
export const buildSubmitGiftsPayload = (args: {
	cartProducts: Array<{ ID: number; QUANTITY: number }>
	selectedOneGifts: Array<{ ID: number; PRODUCT_IDS?: number[] }> | undefined
	allGiftsByProduct: Record<number, Array<{ ID: number }>>
	excludedAllFlagByProduct: Record<number, boolean>
	excludedIdsByProduct: Record<number, number[]>
	type: 'regular' | 'fast'
	routeProductIds?: number[]
	lsMap?: Record<number, number>
}): { one: number[]; all: number[] } => {
	const {
		cartProducts,
		selectedOneGifts,
		allGiftsByProduct,
		excludedAllFlagByProduct,
		excludedIdsByProduct,
		type,
		routeProductIds,
		lsMap
	} = args

	// one: IDs из выбранных подарков one; для fast поддерживаем fallback из LS по productIds из роута
	let oneIds: number[] = (selectedOneGifts || []).map((g: any) => g.ID)
	if ((!oneIds || oneIds.length === 0) && type === 'fast' && routeProductIds && routeProductIds.length) {
		const mapFromLs = (lsMap || {}) as Record<number, number>
		const lsSelected = routeProductIds.map(pid => mapFromLs[pid]).filter((id): id is number => typeof id === 'number')
		oneIds = lsSelected
	}

	// Подготовим мэп выбранных one по productId, чтобы исключить авто-подарки, если one выбран
	const selectedOneByProduct: Record<number, any> = {}
	for (const g of selectedOneGifts || []) {
		const pid = Array.isArray(g?.PRODUCT_IDS) ? g.PRODUCT_IDS[0] : undefined
		if (typeof pid === 'number') selectedOneByProduct[pid] = g
	}

	// all: собираем авто-подарки, уважая флаги исключения и исключённые ID; если есть selected one — auto пропускаем
	const allIdsSet = new Set<number>()
	for (const p of cartProducts) {
		const pid = p.ID
		const autoList: any[] = allGiftsByProduct?.[pid] || []
		if (!autoList.length) continue
		const excludedAll = Boolean(excludedAllFlagByProduct?.[pid])
		const hasSelected = Boolean(selectedOneByProduct[pid])
		if (excludedAll || hasSelected) continue

		const excludedIds = new Set<number>((excludedIdsByProduct?.[pid] || []) as number[])
		for (const g of autoList) {
			if (!excludedIds.has(g.ID)) allIdsSet.add(g.ID)
		}
	}

	const uniq = (arr: number[]) => Array.from(new Set(arr))
	return { one: uniq(oneIds), all: Array.from(allIdsSet) }
}
